import React from 'react';
import { Box, IconButton, Tooltip, Select, MenuItem } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FontListComponent from './FontListComponent'; // Importing FontListComponent
import FormFieldCustomSelectComponent from './FormFieldCustomSelectComponent';

const fontSizeOptions = ['12px', '16px', '24px', '32px', '48px', '64px', '96px'];

const alignmentOptions = [
  { value: 'left', icon: <FormatAlignLeftIcon sx={{ fontSize: '18px' }} /> },
  { value: 'center', icon: <FormatAlignCenterIcon sx={{ fontSize: '18px' }} /> },
  { value: 'right', icon: <FormatAlignRightIcon sx={{ fontSize: '18px' }} /> },
  { value: 'justify', icon: <FormatAlignJustifyIcon sx={{ fontSize: '18px' }} /> },
];

const RichTextEditorToolbar2 = ({ activeStyles, onStyleChange }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        p: 0.5,
        bgcolor: '#f1f3f4',
        borderRadius: 1,
        boxShadow: 1,
        flexWrap: 'wrap',
        '& > *': { flexShrink: 0 },
      }}
    >
      {/* Font Family Selector */}
      <FontListComponent
        activeFont={activeStyles.fontFamily}
        onChange={(font) => onStyleChange('fontFamily', font)}
        context="rich-text-toolbar" // ✅ Explicitly define context
      />

      {/* Font Size Selector */}
      <Select
        value={activeStyles.fontSize}
        onChange={(e) => onStyleChange('fontSize', e.target.value)}
        displayEmpty
        size="small"
        sx={{
          minWidth: 75,
          maxWidth: 75,
          fontSize: '0.75rem',
          border: 'none',
          boxShadow: 'none',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
      >
        {fontSizeOptions.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </Select>

      {/* Bold Button */}
      <Tooltip title="Bold" arrow>
        <IconButton
          onClick={() => onStyleChange('bold', !activeStyles.bold)}
          color={activeStyles.bold ? 'primary' : 'default'}
          sx={{
            p: 0.5,
            '& svg': {
              fontSize: '20px',
            },
          }}
        >
          <FormatBoldIcon />
        </IconButton>
      </Tooltip>

      {/* Italic Button */}
      <Tooltip title="Italic" arrow>
        <IconButton
          onClick={() => onStyleChange('italic', !activeStyles.italic)}
          color={activeStyles.italic ? 'primary' : 'default'}
          sx={{
            p: 0.5,
            '& svg': {
              fontSize: '20px',
            },
          }}
        >
          <FormatItalicIcon />
        </IconButton>
      </Tooltip>

      {/* Underline Button */}
      <Tooltip title="Underline" arrow>
        <IconButton
          onClick={() => onStyleChange('underline', !activeStyles.underline)}
          color={activeStyles.underline ? 'primary' : 'default'}
          sx={{
            p: 0.5,
            '& svg': {
              fontSize: '20px',
            },
          }}
        >
          <FormatUnderlinedIcon />
        </IconButton>
      </Tooltip>

      {/* Font Color Selector */}
      <input
        type="color"
        value={activeStyles.fontColor}
        onChange={(e) => onStyleChange('fontColor', e.target.value)}
        style={{
          width: '24px',
          height: '28px',
          border: 'none',
          padding: '2px',
          borderRadius: '50%',
          cursor: 'pointer',
        }}
      />

      {/* Text Alignment Selector */}
      <Select
        value={activeStyles.textAlign}
        onChange={(e) => onStyleChange('textAlign', e.target.value)}
        displayEmpty
        size="small"
        sx={{
          minWidth: 50,
          fontSize: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          border: 'none',
          boxShadow: 'none',
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
        renderValue={(selected) => {
          const selectedOption = alignmentOptions.find((option) => option.value === selected);
          return selectedOption ? selectedOption.icon : alignmentOptions[0].icon;
        }}
      >
        {alignmentOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ display: 'flex', justifyContent: 'center' }}>
            {option.icon}
          </MenuItem>
        ))}
      </Select>
    
      {/* Inside the return statement of RichTextEditorToolbar2*/}
<FormFieldCustomSelectComponent
  value={activeStyles.customSelect || ''}
  onChange={(selectedField) => onStyleChange('insertCustomChip', selectedField)} // ✅ Pass both label and value
/>

    </Box>
  );
};

export default React.memo(RichTextEditorToolbar2);
